/*
  Collection filter bar.

  Filtering happens entirely in the browser: every dropdown hides cards already
  in the grid rather than asking Shopify to re-query. Infinite scroll keeps
  appending cards, so the options and the count are rebuilt whenever the grid
  changes and converge on the whole collection as it loads.

  Sold-out cards are excluded from the count when the section hides them, so the
  number always reflects what is actually purchasable.
*/
const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2X', '3X'];

class CollectionFilterBar extends HTMLElement {
  connectedCallback() {
    this.hideSoldOut = this.dataset.hideSoldOut === 'true';
    this.grid = document.getElementById('product-grid');

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);

    this.buildOptions();
    this.apply();

    // Infinite scroll appends cards after load, so the brand/type/size lists and
    // the count both have to be rebuilt as the grid grows.
    if (this.grid) {
      this.observer = new MutationObserver((mutations) => {
        if (!mutations.some((mutation) => mutation.addedNodes.length > 0)) return;
        this.buildOptions();
        this.apply();
      });
      this.observer.observe(this.grid, { childList: true });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    if (this.observer) this.observer.disconnect();
  }

  get selects() {
    return Array.from(this.querySelectorAll('select[data-filter-param]'));
  }

  select(name) {
    return this.querySelector(`select[data-filter-param="${name}"]`);
  }

  get cards() {
    return this.grid ? Array.from(this.grid.querySelectorAll('[data-product-id]')) : [];
  }

  onChange = (event) => {
    if (event.target.closest('select[data-filter-param]')) this.apply();
  };

  onClick = (event) => {
    if (event.target.closest('[data-filter-open]')) return this.setPanel(true);
    if (event.target.closest('[data-filter-close]')) return this.setPanel(false);

    // The scrim is a ::before on this host, so its clicks land on the host itself.
    if (event.target === this) return this.setPanel(false);

    if (event.target.closest('[data-filter-clear]')) {
      this.selects.forEach((select) => {
        select.value = '';
      });
      this.apply();
    }
  };

  /* ---------- options ---------- */

  buildOptions() {
    const brands = new Map();
    const types = new Set();
    const sizes = new Set();

    this.cards.forEach((card) => {
      // Sold-out cards are hidden from the grid, so they must not contribute
      // options a shopper could pick and then get an empty result from.
      if (this.hideSoldOut && card.dataset.available === 'false') return;

      const vendor = (card.dataset.productVendor || '').trim();
      // Vendors arrive inconsistently cased ("ASTRUM" vs "Astrum"); key on the
      // uppercase form so they collapse into a single option.
      if (vendor) brands.set(vendor.toUpperCase(), vendor);

      const type = (card.dataset.productType || '').trim();
      if (type) types.add(type);

      (card.dataset.availableSizes || '')
        .split(',')
        .map((size) => size.trim())
        .filter(Boolean)
        .forEach((size) => sizes.add(size));
    });

    this.fill('brand', Array.from(brands.keys()).sort().map((key) => [key, brands.get(key)]));
    this.fill('type', Array.from(types).sort().map((type) => [type, type]));
    this.fill(
      'size',
      Array.from(sizes)
        .sort((a, b) => CollectionFilterBar.sizeIndex(a) - CollectionFilterBar.sizeIndex(b))
        .map((size) => [size, size])
    );
  }

  fill(name, entries) {
    const select = this.select(name);
    if (!select) return;

    const previous = select.value;
    const placeholder = select.options[0];

    select.textContent = '';
    if (placeholder) select.appendChild(placeholder);

    entries.forEach(([value, label]) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      select.appendChild(option);
    });

    // Keep the shopper's choice across rebuilds, but drop it if the growing grid
    // no longer offers it.
    select.value = entries.some(([value]) => value === previous) ? previous : '';
  }

  static sizeIndex(size) {
    const known = SIZE_ORDER.indexOf(size);
    if (known !== -1) return known;
    const numeric = parseInt(size, 10);
    return Number.isNaN(numeric) ? 2000 : 1000 + numeric;
  }

  /* ---------- filtering ---------- */

  apply() {
    const brand = this.select('brand') ? this.select('brand').value : '';
    const type = this.select('type') ? this.select('type').value : '';
    const size = this.select('size') ? this.select('size').value : '';

    this.cards.forEach((card) => {
      const cardSizes = (card.dataset.availableSizes || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);

      const matches =
        (!brand || (card.dataset.productVendor || '').trim().toUpperCase() === brand) &&
        (!type || (card.dataset.productType || '').trim() === type) &&
        (!size || cardSizes.indexOf(size) !== -1);

      card.classList.toggle('collection-filters__hidden', !matches);
    });

    this.updateCount();
    this.updateActiveCount();
  }

  /* ---------- count ---------- */

  updateCount() {
    const target = this.querySelector('[data-product-count]');
    if (!target) return;

    // Unfiltered, the server already knows the real in-stock total for the whole
    // collection; counting the DOM would only ever see what has loaded so far.
    if (!this.selects.some((select) => select.value)) {
      target.textContent = target.dataset.serverCount || target.textContent;
      return;
    }

    target.textContent = this.formatCount(this.countVisible());
  }

  countVisible() {
    return this.cards.filter((card) => {
      if (card.classList.contains('collection-filters__hidden')) return false;
      if (this.hideSoldOut && card.dataset.available === 'false') return false;
      return true;
    }).length;
  }

  formatCount(total) {
    const template = total === 1 ? this.dataset.countOne : this.dataset.countOther;
    if (!template) return String(total);
    // Match the number plus any non-breaking thousands separators, but not the
    // ordinary space before the word, or "2 products" becomes "6products".
    return template.replace(/\d[\d.,\u00A0\u202F]*/, String(total));
  }

  updateActiveCount() {
    const badge = this.querySelector('[data-filter-active-count]');
    if (!badge) return;

    const active = this.selects.filter((select) => select.value).length;
    badge.textContent = active ? `(${active})` : '';
    badge.hidden = active === 0;
  }

  /* ---------- mobile panel ---------- */

  setPanel(open) {
    this.classList.toggle('collection-filters--open', open);

    const toggle = this.querySelector('[data-filter-open]');
    if (toggle) toggle.setAttribute('aria-expanded', String(open));

    // The lock rule only exists inside the sheet breakpoint, so this class is
    // inert on desktop and needs no width check here.
    document.body.classList.toggle('collection-filters-locked', open);

    if (open) {
      const first = this.querySelector('.collection-filters__panel select');
      if (first) first.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('collection-filter-bar')) {
  customElements.define('collection-filter-bar', CollectionFilterBar);
}
